import { Capacitor } from '@capacitor/core'
import { Preferences } from '@capacitor/preferences'

type AuthPersistKey = 'token' | 'auth_user' | 'descope_session'

// `descope_session` is the Descope-issued side-session that authorizes MFA calls when the
// primary session came from a legacy Member Hub login (see use-descope-session.js). It is
// restored alongside the others so MFA stays available after iOS clears WKWebView
// localStorage on app update — otherwise an enrolled user would be silently unable to
// complete or manage their second factor until they logged in again.
const AUTH_PERSIST_KEYS: AuthPersistKey[] = ['token', 'auth_user', 'descope_session']

export interface NativeAuthPersistence {
  set(key: AuthPersistKey, value: string): void
  remove(key: AuthPersistKey): void
}

async function restoreAuthData () {
  await Promise.all(
    AUTH_PERSIST_KEYS.map(async key => {
      try {
        if (!localStorage.getItem(key)) {
          const { value } = await Preferences.get({ key })
          if (value) {
            localStorage.setItem(key, value)
          }
        }
      } catch {
        // Best-effort — don't let one key failure block the other
      }
    })
  )
}

export default defineNuxtPlugin(async () => {
  let persistence: NativeAuthPersistence | null = null

  if (Capacitor.isNativePlatform()) {
    // Restore auth data from Capacitor Preferences to localStorage if missing
    // This handles the case where iOS clears WKWebView localStorage on app update
    try {
      await restoreAuthData()
    } catch {
      // Best-effort recovery — app continues without restored auth
    }

    persistence = {
      set (key: AuthPersistKey, value: string) {
        if (AUTH_PERSIST_KEYS.includes(key)) {
          // eslint-disable-next-line no-console
          Preferences.set({ key, value }).catch(console.error)
        }
      },
      remove (key: AuthPersistKey) {
        if (AUTH_PERSIST_KEYS.includes(key)) {
          // eslint-disable-next-line no-console
          Preferences.remove({ key }).catch(console.error)
        }
      }
    }
  }

  return {
    provide: {
      nativeAuthPersistence: persistence
    }
  }
})
